import { useState, useRef } from "react";
import { Camera, Trash2, Loader2, X } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

const MAX_PHOTOS = 10;

export default function EntryPhotoUpload({ entryId, photos = [], onPhotosChange }) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per entry`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/collection/${entryId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded!");
      onPhotosChange?.([...photos, data.photo]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (photoId) => {
    setDeletingId(photoId);
    try {
      await api.delete(`/collection/${entryId}/photos/${photoId}`);
      toast.success("Photo removed");
      onPhotosChange?.(photos.filter((p) => p.photo_id !== photoId));
      if (lightbox === photoId) setLightbox(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid={`entry-photos-${entryId}`}>
      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {photos.map((photo) => (
            <div key={photo.photo_id} className="relative group/photo">
              <img
                src={resolveImageUrl(photo.url)}
                alt="Collection photo"
                className="w-16 h-16 rounded-lg object-cover cursor-pointer border border-zinc-700/30 hover:border-zinc-500 transition-colors"
                onClick={() => setLightbox(photo.photo_id)}
                data-testid={`photo-thumb-${photo.photo_id}`}
              />
              <button
                data-testid={`delete-photo-${photo.photo_id}`}
                onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }}
                disabled={deletingId === photo.photo_id}
                className="absolute -top-1.5 -right-1.5 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 opacity-0 group-hover/photo:opacity-100 transition-opacity hover:bg-rose-600 hover:border-rose-600"
              >
                {deletingId === photo.photo_id ? (
                  <Loader2 className="w-3 h-3 text-zinc-400 animate-spin" />
                ) : (
                  <X className="w-3 h-3 text-zinc-400" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      <div className="mt-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleUpload} data-testid={`photo-file-input-${entryId}`} />
        <button
          data-testid={`upload-photo-btn-${entryId}`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading || photos.length >= MAX_PHOTOS}
          className="flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</>
          ) : (
            <><Camera className="w-3 h-3" /> {photos.length > 0 ? "Add photo" : "Add photos"} ({photos.length}/{MAX_PHOTOS})</>
          )}
        </button>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={() => setLightbox(null)}>
            <X className="w-8 h-8" />
          </button>
          <img
            src={resolveImageUrl(photos.find((p) => p.photo_id === lightbox)?.url)}
            alt="Full size"
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute bottom-6 bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
            onClick={(e) => { e.stopPropagation(); handleDelete(lightbox); }}
          >
            <Trash2 className="w-4 h-4" /> Delete Photo
          </button>
        </div>
      )}
    </div>
  );
}
